// src/components/blog/BlogCard.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { designTokens } from '../../theme/designTokens';
import { Calendar, Clock, ArrowRight } from 'lucide-react';
import {getCategoryColor} from "../../utils/blogUtils";

const BlogCard = ({ blog, onClick, featured = false }) => {
    const categoryColor = getCategoryColor(blog.category);

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -5 }}
            onClick={onClick}
            style={{
                borderRadius: '1rem',
                overflow: 'hidden',
                background: 'white',
                border: `1px solid ${designTokens.colors.border.light}`,
                boxShadow: designTokens.shadows.subtle,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {/* Featured Badge */}
            {featured && (
                <div style={{
                    position: 'absolute',
                    top: '1rem',
                    left: '1rem',
                    zIndex: 2,
                    padding: '0.25rem 0.75rem',
                    borderRadius: '9999px',
                    background: designTokens.colors.champagne,
                    color: 'white',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    letterSpacing: '0.05em',
                }}>
                    Featured
                </div>
            )}

            {/* Cover Image */}
            {blog.coverImage?.url && (
                <div
                    style={{
                        width: '100%',
                        maxWidth: '100%',
                        aspectRatio: '1 / 1',   // perfect square container
                        overflow: 'hidden',
                        borderRadius: '12px',
                        background: '#fff',
                        position: 'relative',
                    }}
                >
                    <img
                        src={`${blog.coverImage.url}?tr=w-1200,q-80,f-auto`}
                        srcSet={`
        ${blog.coverImage.url}?tr=w-600,q-75,f-auto 600w,
        ${blog.coverImage.url}?tr=w-1200,q-80,f-auto 1200w,
        ${blog.coverImage.url}?tr=w-1600,q-85,f-auto 1600w
      `}
                        sizes="(max-width: 768px) 100vw, 1200px"
                        alt={blog.coverImage.alt || blog.title}
                        width="1200"
                        height="1200"
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',   // show full image, no crop
                            transition: 'transform 0.4s ease',
                        }}
                        onMouseEnter={(e) => (e.target.style.transform = 'scale(1.05)')}
                        onMouseLeave={(e) => (e.target.style.transform = 'scale(1)')}
                        loading="eager"
                        fetchPriority="high"
                    />
                </div>
            )}

            {/* Content */}
            <div style={{
                padding: '1.5rem',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
            }}>
                {/* Category */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '0.75rem',
                }}>
                    <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: categoryColor,
                    }} />
                    <span style={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        color: categoryColor,
                    }}>
            {blog.category}
          </span>
                </div>

                {/* Title */}
                <h3 style={{
                    fontFamily: designTokens.typography.fontFamilies.display,
                    fontSize: '1.25rem',
                    fontWeight: 400,
                    color: designTokens.colors.text.primary,
                    marginBottom: '0.75rem',
                    lineHeight: 1.4,
                    flex: 1,
                }}>
                    {blog.title}
                </h3>

                {/* Excerpt */}
                <p style={{
                    fontSize: '0.875rem',
                    color: designTokens.colors.text.secondary,
                    marginBottom: '1rem',
                    lineHeight: 1.6,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    flex: 1,
                }}>
                    {blog.excerpt}
                </p>

                {/* Meta Info */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginTop: 'auto',
                    paddingTop: '1rem',
                    borderTop: `1px solid ${designTokens.colors.border.light}`,
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            fontSize: '0.75rem',
                            color: designTokens.colors.text.tertiary,
                        }}>
                            <Calendar size={12} />
                            <span>
                {new Date(blog.publishedAt || blog.date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric'
                })}
              </span>
                        </div>

                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            fontSize: '0.75rem',
                            color: designTokens.colors.text.tertiary,
                        }}>
                            <Clock size={12} />
                            <span>{blog.readingTime || 5} min</span>
                        </div>
                    </div>

                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        fontSize: '0.75rem',
                        color: designTokens.colors.champagne,
                        fontWeight: 500,
                    }}>
                        Read <ArrowRight size={12} />
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

export default BlogCard;